import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { FeedbackProvider } from '@/contexts/FeedbackContext';
import { Toaster } from '@/components/ui/toaster';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import { checkForAbandonedAssessment, setupAbandonmentListener } from '@/utils/amplitudeEvents';
import { captureAttribution } from '@/utils/marketingAttribution';

import { RouteSkeleton } from '@/components/skeletons/RouteSkeleton';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import ErrorBoundary from '@/components/ErrorBoundary';
import { logger } from '@/utils/logger';
import ProtectedRoute from '@/components/ProtectedRoute';
import NotFound from '@/pages/NotFound';
import HomePage from '@/pages/HomePage';
import PersistentAdminLayout from '@/components/layouts/PersistentAdminLayout';
import AdLayout from '@/pages/AdLayout';
const AdLandingPage = React.lazy(() => import('@/pages/AdLandingPage'));
const AdQualificationStep = React.lazy(() => import('@/pages/AdQualificationStep'));

// Route configurations
import { authRoutes } from '@/config/routes/authRoutes';
import { staticPublicRoutes, lazyPublicRoutes } from '@/config/routes/publicRoutes';
import { staticAssessmentRoutes, lazyAssessmentRoutes } from '@/config/routes/assessmentRoutes';
import { staticAdminRoutes, lazyAdminRoutes } from '@/config/routes/adminRoutes';
import { superAdminRoutes } from '@/config/routes/superAdminRoutes';

// QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  // Assessment abandonment detection: check on mount (fallback) + visibilitychange (primary)
  useEffect(() => {
    captureAttribution();
    checkForAbandonedAssessment();
    const cleanup = setupAbandonmentListener();
    return cleanup;
  }, []);

  const handleFeedbackSubmit = (feedback: any) => {
    logger.info('Feedback submitted:', feedback);
  };

  return (
    <ErrorBoundary>
      <Router>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
          >
            <LanguageProvider>
              <AuthProvider>
                <AnalyticsProvider>
                  <CompanyProvider>
                    <SubscriptionProvider>
                      <FeedbackProvider onFeedbackSubmit={handleFeedbackSubmit}>
                        <div className="min-h-screen bg-background">
                          <MaintenanceBanner />
                          <Toaster />
                          <Routes>
                            {/* Home */}
                            <Route path="/" element={
                              <RouteErrorBoundary section="Home">
                                <HomePage />
                              </RouteErrorBoundary>
                            } />
                            
                            {/* Auth routes */}
                            {authRoutes.map(({ path, component: Component }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <RouteErrorBoundary section="Auth">
                                    <Component />
                                  </RouteErrorBoundary>
                                } 
                              />
                            ))}
                            
                            {/* Static public routes */}
                            {staticPublicRoutes.map(({ path, component: Component }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <RouteErrorBoundary section="Public">
                                    <Component />
                                  </RouteErrorBoundary>
                                } 
                              />
                            ))}
                            
                            {/* Lazy public routes */}
                            {lazyPublicRoutes.map(({ path, component: Component }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <RouteErrorBoundary section="Public">
                                    <Suspense fallback={<RouteSkeleton />}>
                                      <Component />
                                    </Suspense>
                                  </RouteErrorBoundary>
                                } 
                              />
                            ))}
                            
                            {/* Ad funnel routes — shared AdSignupContext keeps password in memory only */}
                            <Route element={<AdLayout />}>
                              <Route
                                path="/ad"
                                element={
                                  <RouteErrorBoundary section="Public">
                                    <Suspense fallback={<RouteSkeleton />}>
                                      <AdLandingPage />
                                    </Suspense>
                                  </RouteErrorBoundary>
                                }
                              />
                              <Route
                                path="/ad/step"
                                element={
                                  <RouteErrorBoundary section="Public">
                                    <Suspense fallback={<RouteSkeleton />}>
                                      <AdQualificationStep />
                                    </Suspense>
                                  </RouteErrorBoundary>
                                }
                              />
                            </Route>

                            {/* Static assessment routes */}
                            {staticAssessmentRoutes.map(({ path, component: Component }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <RouteErrorBoundary section="Assessment">
                                    <Component />
                                  </RouteErrorBoundary>
                                } 
                              />
                            ))}
                            
                            {/* Lazy assessment routes */}
                            {lazyAssessmentRoutes.map(({ path, component: Component, skeletonRoute }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <RouteErrorBoundary section="Assessment">
                                    <Suspense fallback={<RouteSkeleton route={skeletonRoute} />}>
                                      <Component />
                                    </Suspense>
                                  </RouteErrorBoundary>
                                } 
                              />
                            ))}
                            
                            {/* Admin routes - wrapped in persistent layout */}
                            <Route element={
                              <ProtectedRoute keepChildrenMountedWhileLoading>
                                <PersistentAdminLayout />
                              </ProtectedRoute>
                            }>
                              {/* Static admin routes */}
                              {staticAdminRoutes.map(({ path, component: Component, requireSubscription }) => (
                                <Route 
                                  key={path} 
                                  path={path} 
                                  element={
                                    <RouteErrorBoundary section="Admin">
                                      <Component />
                                    </RouteErrorBoundary>
                                  } 
                                />
                              ))}
                              
                              {/* Lazy admin routes */}
                              {lazyAdminRoutes.map(({ path, component: Component, skeletonRoute }) => (
                                <Route 
                                  key={path} 
                                  path={path} 
                                  element={
                                    <RouteErrorBoundary section="Admin">
                                      <Suspense fallback={<RouteSkeleton route={skeletonRoute} />}>
                                        <Component />
                                      </Suspense>
                                    </RouteErrorBoundary>
                                  } 
                                />
                              ))}
                            </Route>
                            
                            {/* Super admin routes */}
                            {superAdminRoutes.map(({ path, component: Component }) => (
                              <Route 
                                key={path} 
                                path={path} 
                                element={
                                  <ProtectedRoute requireSuperAdmin>
                                    <RouteErrorBoundary section="SuperAdmin">
                                      <Suspense fallback={<RouteSkeleton />}>
                                        <Component />
                                      </Suspense>
                                    </RouteErrorBoundary>
                                  </ProtectedRoute>
                                } 
                              />
                            ))}
                            
                            {/* Fallback */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </div>
                      </FeedbackProvider>
                    </SubscriptionProvider>
                  </CompanyProvider>
                </AnalyticsProvider>
              </AuthProvider>
            </LanguageProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
